import {
  TIMELINE_MIN_BLOCK_WIDTH_PX,
  TIMELINE_MIN_SHORT_BLOCK_WIDTH_PX,
  TIMELINE_SHORT_CLASS_MAX_MINUTES,
  TIMELINE_WINDOW_MINUTES,
  toTimelineEndOffset,
  toTimelineStartOffset,
} from './timeline';
import { type OverviewEvent, type PositionedOverviewEvent } from './types';

export function getTimelinePosition(
  startMinutes: number,
  endMinutes: number,
  timelineWidthPx: number,
): { leftPx: number; widthPx: number } {
  const startOffset = toTimelineStartOffset(startMinutes);
  const endOffset = toTimelineEndOffset(endMinutes, startMinutes);

  if (endOffset <= startOffset) {
    return { leftPx: 0, widthPx: TIMELINE_MIN_BLOCK_WIDTH_PX };
  }

  const leftPx = (startOffset / TIMELINE_WINDOW_MINUTES) * timelineWidthPx;
  const rawWidth =
    ((endOffset - startOffset) / TIMELINE_WINDOW_MINUTES) * timelineWidthPx;

  return {
    leftPx,
    widthPx: rawWidth,
  };
}

export function getBlockLayout(
  startMinutes: number,
  endMinutes: number,
  nextEventStartMinutes: number | null,
  timelineWidthPx: number,
): { leftPx: number; widthPx: number } {
  const { leftPx, widthPx: timeWidthPx } = getTimelinePosition(
    startMinutes,
    endMinutes,
    timelineWidthPx,
  );

  const startOffset = toTimelineStartOffset(startMinutes);
  const endOffset = toTimelineEndOffset(endMinutes, startMinutes);
  const durationMinutes = endOffset - startOffset;
  const preferredMinWidth =
    durationMinutes <= TIMELINE_SHORT_CLASS_MAX_MINUTES
      ? TIMELINE_MIN_SHORT_BLOCK_WIDTH_PX
      : TIMELINE_MIN_BLOCK_WIDTH_PX;

  if (nextEventStartMinutes !== null) {
    const nextStartOffset = toTimelineStartOffset(nextEventStartMinutes);
    const maxWidthPx =
      ((nextStartOffset - startOffset) / TIMELINE_WINDOW_MINUTES) *
      timelineWidthPx;

    return {
      leftPx,
      widthPx: Math.max(
        1,
        Math.min(
          Math.max(timeWidthPx, Math.min(preferredMinWidth, maxWidthPx)),
          maxWidthPx,
        ),
      ),
    };
  }

  return {
    leftPx,
    widthPx: Math.max(preferredMinWidth, timeWidthPx),
  };
}

export function applyBlockLayoutToLanes(
  lanes: Array<Array<OverviewEvent>>,
  timelineWidthPx: number,
): Array<Array<PositionedOverviewEvent>> {
  return lanes.map((lane) => {
    const sortedLane = [...lane].sort(
      (a, b) =>
        toTimelineStartOffset(a.startMinutes) -
        toTimelineStartOffset(b.startMinutes),
    );

    return sortedLane.map((event, eventIndex) => {
      const nextEvent = sortedLane[eventIndex + 1];
      const { leftPx, widthPx } = getBlockLayout(
        event.startMinutes,
        event.endMinutes,
        nextEvent?.startMinutes ?? null,
        timelineWidthPx,
      );

      return { event, leftPx, widthPx };
    });
  });
}
